// /customers/rfm — RFM Dashboard (PAGES.md §3.81) — OWNER (50)
//
// Hero + 4 KPI tiles + donut distribution + migration trend + insight cards.
// Reuses ReportPageShell / ReportKpiRow so the visual language is identical
// to the Reports sprint.

import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { Badge, EmptyState, PieChart, SalesWaveChart, Select } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import {
  ReportPageShell,
  ReportKpiRow,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  KpiIcons,
  downloadCsv,
  shareReportLink,
} from '@/features/reports';
import {
  useRfmAnalysis,
  RfmSegmentCard,
  segmentMeta,
  RFM_SEGMENT_ORDER,
} from '@/features/customers';
import type { RfmAnalysisData, RfmSegmentRow } from '@/features/customers';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Period = RfmAnalysisData['period'];

const PERIOD_OPTIONS = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 180 days' },
  { value: '365d', label: 'Last 12 months' },
];

// Map RFM segment → chart color (matches the gradient starts in segments.ts)
const SEGMENT_COLOR: Record<string, string> = {
  CHAMPION: '#f59e0b',
  LOYAL: '#10b981',
  POTENTIAL_LOYALIST: '#3b82f6',
  AT_RISK: '#ef4444',
  INACTIVE: '#94a3b8',
};

function makeSegmentTableColumns(currency: CurrencyCode): ColumnDef<RfmSegmentRow>[] {
  return [
  {
    key: 'segment',
    header: 'Segment',
    render: (_v, row) => {
      const meta = segmentMeta(row.segment);
      return meta ? (
        <Badge variant={meta.kpiTone === 'warning' ? 'warning' : meta.kpiTone === 'success' ? 'success' : meta.kpiTone === 'danger' ? 'danger' : meta.kpiTone === 'neutral' ? 'voided' : 'info'} size="sm">
          {meta.label}
        </Badge>
      ) : <span>{row.segment}</span>;
    },
  },
  {
    key: 'count',
    header: 'Customers',
    numeric: true,
    render: (_v, row) => <span className="font-mono tabular-nums font-semibold">{row.count.toLocaleString('en-IN')}</span>,
  },
  {
    key: 'avg_spend',
    header: 'Avg Spend',
    numeric: true,
    render: (_v, row) => <span className="font-mono tabular-nums">{formatPaise(row.avg_spend, currency)}</span>,
  },
  {
    key: 'avg_visits',
    header: 'Avg Visits',
    numeric: true,
    render: (_v, row) => <span className="font-mono tabular-nums">{row.avg_visits.toFixed(1)}</span>,
  },
  {
    key: 'avg_recency_days',
    header: 'Avg Recency (days)',
    numeric: true,
    render: (_v, row) => <span className="font-mono tabular-nums">{row.avg_recency_days}</span>,
  },
  {
    key: 'recommended_action',
    header: 'Recommended Action',
    render: (_v, row) => <span className="text-sm text-neutral-700 dark:text-neutral-300 max-w-xs truncate">{row.recommended_action}</span>,
  },
  ];
}

export function RfmDashboardPage() {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const segmentTableColumns = useMemo(() => makeSegmentTableColumns(currency), [currency]);
  const [period, setPeriod] = useState<Period>('90d');
  const { data, isLoading, error } = useRfmAnalysis(period);

  const segmentsByKey = useMemo(() => {
    const map = new Map<string, RfmSegmentRow>();
    (data?.segments ?? []).forEach((s) => map.set(s.segment, s));
    return map;
  }, [data]);

  // Order cards deterministically regardless of backend ordering
  const orderedSegments = useMemo(
    () =>
      RFM_SEGMENT_ORDER
        .map((id) => segmentsByKey.get(id))
        .filter((s): s is RfmSegmentRow => Boolean(s)),
    [segmentsByKey],
  );

  const total = data?.total_customers ?? 0;
  const champion = segmentsByKey.get('CHAMPION')?.count ?? 0;
  const atRisk = segmentsByKey.get('AT_RISK')?.count ?? 0;
  const inactive = segmentsByKey.get('INACTIVE')?.count ?? 0;

  const donutData = orderedSegments.map((s) => ({
    label: segmentMeta(s.segment)!.label,
    value: s.count,
    color: SEGMENT_COLOR[s.segment],
  }));

  const migrationData = (data?.migration_trend ?? []).map((p) => ({
    label: p.date.slice(5),
    today: p.champion + p.loyal,
    yesterday: p.at_risk + p.inactive,
  }));

  function handleExport(fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') {
    if (fmt === 'csv') {
      downloadCsv(
        `rfm-analysis-${period}.csv`,
        [
          { header: 'Segment', get: (r: RfmSegmentRow) => segmentMeta(r.segment)?.label ?? r.segment },
          { header: 'Customers', get: (r: RfmSegmentRow) => r.count },
          { header: `Avg Spend (${symbol})`, get: (r: RfmSegmentRow) => (r.avg_spend / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) },
          { header: 'Avg Visits', get: (r: RfmSegmentRow) => r.avg_visits.toFixed(1) },
          { header: 'Avg Recency (days)', get: (r: RfmSegmentRow) => r.avg_recency_days },
        ],
        orderedSegments,
      );
      return;
    }
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(
        fmt,
        'Orio360 RFM Analysis',
        `${total} customers • Champions: ${champion} • At Risk: ${atRisk} • Period: ${period}`,
      );
    }
  }

  return (
    <ReportPageShell
      title="RFM Dashboard"
      subtitle="Recency, Frequency and Monetary segmentation with per-segment actions and migration trend."
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Customers', href: '/customers' },
        { label: 'RFM Dashboard' },
      ]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={orderedSegments.length === 0} />}
      filterBar={
        <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Analysis Period</span>
            <div className="w-full sm:w-52">
              <Select
                options={PERIOD_OPTIONS}
                value={period}
                onChange={(v) => setPeriod(v as Period)}
              />
            </div>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : total === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="Not enough customer data for RFM analysis"
            description="RFM analysis requires at least 50 customers with purchase history."
            icon={
              <svg className="h-20 w-20 text-violet-300 dark:text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            action={{ label: 'View Customers', onClick: () => navigate('/customers') }}
          />
        </div>
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'total', title: 'Total Customers', value: total.toLocaleString('en-IN'), tone: 'indigo', icon: KpiIcons.users },
              { id: 'champion', title: 'Champions', value: champion.toLocaleString('en-IN'), subLabel: `${total > 0 ? ((champion / total) * 100).toFixed(1) : '0'}% of base`, tone: 'warning', icon: KpiIcons.star },
              { id: 'atrisk', title: 'At Risk', value: atRisk.toLocaleString('en-IN'), subLabel: 'Needs win-back', tone: 'danger', icon: KpiIcons.trendDown },
              { id: 'inactive', title: 'Inactive', value: inactive.toLocaleString('en-IN'), subLabel: 'Likely churned', tone: 'neutral', icon: KpiIcons.clock },
            ]}
          />

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            {donutData.length > 0 ? (
              <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-5 animate-slide-up">
                <PieChart data={donutData} title="Segment Distribution" donut />
              </div>
            ) : null}
            {migrationData.length > 0 ? (
              <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-5 animate-slide-up">
                <SalesWaveChart data={migrationData} valueFormat="number" title="Healthy vs At-Risk Segment Trend" />
              </div>
            ) : null}
          </div>

          {/* Per-segment insight cards */}
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-2">
            <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-violet-500 to-pink-500" />
            Segment Insights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 mb-6">
            {orderedSegments.map((s, idx) => (
              <RfmSegmentCard key={s.segment} row={s} totalCustomers={total} animationDelay={idx * 50} period={period} onCreateCampaign={() => navigate(`/communications/campaigns?segment=${s.segment}`)} />
            ))}
          </div>

          {/* Segment DataTable */}
          {orderedSegments.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden mb-6 animate-slide-up">
              <div className="px-5 pt-5 pb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                  <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-indigo-500 to-violet-500" />
                  Segment Breakdown
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/80">
                      {segmentTableColumns.map((col) => (
                        <th key={col.key} className={`px-4 py-3 text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 ${col.numeric ? 'text-right' : 'text-left'}`}>{col.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orderedSegments.map((row) => (
                      <tr key={row.segment} className="border-b border-neutral-50 dark:border-neutral-700/50 even:bg-neutral-50/50 dark:even:bg-neutral-800/30 hover:bg-brand-50 dark:hover:bg-brand-900/10 transition-colors">
                        {segmentTableColumns.map((col) => (
                          <td key={col.key} className={`px-4 py-3 ${col.numeric ? 'text-right' : 'text-left'}`}>
                            {col.render ? col.render((row as unknown as Record<string, unknown>)[col.key], row, 0) : String((row as unknown as Record<string, unknown>)[col.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Aggregate KPIs footer */}
          <div className="rounded-2xl bg-gradient-to-br from-indigo-50 via-violet-50 to-pink-50 dark:from-indigo-900/10 dark:via-violet-900/10 dark:to-pink-900/10 p-5 ring-1 ring-violet-100 dark:ring-violet-800/30 animate-slide-up">
            <p className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Portfolio Summary</p>
            <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-200">
              Across <span className="font-bold tabular-nums">{total.toLocaleString('en-IN')}</span> customers, average lifetime value per segment is{' '}
              <span className="font-bold tabular-nums">
                {formatPaise(
                  orderedSegments.length
                    ? Math.round(orderedSegments.reduce((sum, s) => sum + s.avg_spend * s.count, 0) / total)
                    : 0,
                  currency,
                )}
              </span>. Focus win-back on <span className="font-semibold text-warning-700 dark:text-warning-300">At Risk</span> and
              reward retention on <span className="font-semibold text-amber-700 dark:text-amber-300">Champions</span>.
            </p>
          </div>
        </>
      )}
    </ReportPageShell>
  );
}
